import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Sparkles, Send, Pin, PinOff, BookmarkPlus, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { MicButton } from '@/components/MicButton';
import { coachApi } from '@/lib/api';
import { toast } from 'sonner';

const suggestions = [
  { label: 'Stretch my back', prompt: 'Give me a quick stretch for lower-back tightness right now.' },
  { label: 'Bicep workout ideas', prompt: 'Suggest a short home bicep workout with good form cues.' },
  { label: 'Is this meal good?', prompt: 'Is this meal good for my health? Give me a verdict and 1-2 swaps.' },
];

const CoachChat = ({ open, onOpenChange, context }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [routines, setRoutines] = useState([]);
  const [showRoutines, setShowRoutines] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0 && context?.meal) {
      setMessages([{ role: 'ai', text: `I see you had "${context.meal}". Ask me anything about it — health impact, alternatives, portion, or ingredients.` }]);
    }
  }, [open, context, messages.length]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const refreshRoutines = async () => {
    try {
      const { items } = await coachApi.listRoutines();
      setRoutines(items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (open) refreshRoutines(); }, [open]);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setMessages((m) => [...m, { role: 'user', text: question, question }]);
    setInput('');
    setLoading(true);
    try {
      const { reply } = await coachApi.chat({ question, context });
      setMessages((m) => [...m, { role: 'ai', text: reply, question, saved: false }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Coach is resting, try again');
    } finally { setLoading(false); }
  };

  const pinMessage = async (idx) => {
    const msg = messages[idx];
    if (!msg || msg.role !== 'ai' || msg.saved) return;
    const title = (msg.question || msg.text).slice(0, 60);
    try {
      const saved = await coachApi.saveRoutine({ title, question: msg.question || '', reply: msg.text });
      setMessages((m) => m.map((x, i) => (i === idx ? { ...x, saved: true, id: saved.id } : x)));
      setRoutines((r) => [saved, ...r]);
      toast.success('Pinned to My Routines');
    } catch (e) {
      toast.error('Could not pin this answer');
    }
  };

  const removeRoutine = async (id) => {
    try {
      await coachApi.deleteRoutine(id);
      setRoutines((r) => r.filter((x) => x.id !== id));
      setMessages((m) => m.map((x) => (x.id === id ? { ...x, saved: false, id: undefined } : x)));
      toast.success('Removed');
    } catch { toast.error('Could not remove'); }
  };

  const revisit = (r) => {
    setMessages((m) => [...m, { role: 'user', text: r.title }, { role: 'ai', text: r.reply, question: r.question, saved: true, id: r.id }]);
    setShowRoutines(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#FAF9F6] border-[#E5E1D8] rounded-2xl" data-testid="coach-chat-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-medium text-[#1A2421] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#4A7C59]" strokeWidth={1.5} /> Ask your coach
          </DialogTitle>
        </DialogHeader>

        {/* My Routines toggle */}
        <button
          onClick={() => setShowRoutines((v) => !v)}
          className="w-full flex items-center justify-between text-xs tracking-[0.15em] uppercase font-semibold text-[#4A5A53] px-3 py-2 rounded-full bg-white border border-[#E5E1D8]"
          data-testid="coach-routines-toggle"
        >
          <span className="flex items-center gap-2">
            <Pin className="w-3.5 h-3.5 text-[#4A7C59]" strokeWidth={1.8} />
            My Routines
            {routines.length > 0 && <span className="text-[#4A7C59]">· {routines.length}</span>}
          </span>
          {showRoutines ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showRoutines && (
          <div className="max-h-40 overflow-y-auto space-y-2 pr-1" data-testid="coach-routines-panel">
            {routines.length === 0 && (
              <div className="text-xs text-[#7E9979] italic px-1">Pin any coach reply and it will live here — a stretch, a workout, anything you'll want again.</div>
            )}
            {routines.map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-2 p-2 rounded-xl bg-white border border-[#E5E1D8]"
                data-testid={`routine-item-${r.id}`}
              >
                <button
                  onClick={() => revisit(r)}
                  className="flex-1 text-left text-sm text-[#1A2421] truncate hover:text-[#4A7C59]"
                  title={r.reply}
                >
                  {r.title}
                </button>
                <button
                  onClick={() => removeRoutine(r.id)}
                  className="text-[#C05746] hover:text-[#8B3D30]"
                  data-testid={`routine-delete-${r.id}`}
                  aria-label="Remove routine"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="max-h-72 overflow-y-auto pr-1 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`text-sm leading-relaxed ${m.role === 'user' ? 'text-right' : ''}`}>
              <div className={`inline-block px-4 py-2.5 rounded-2xl ${m.role === 'user' ? 'bg-[#4A7C59] text-white' : 'bg-white border border-[#E5E1D8] text-[#1A2421] whitespace-pre-wrap'}`}>
                {m.text}
              </div>
              {m.role === 'ai' && (
                <div className="mt-1">
                  <button
                    onClick={() => (m.saved ? removeRoutine(m.id) : pinMessage(i))}
                    className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${m.saved ? 'bg-[#4A7C59] text-white border-[#4A7C59]' : 'bg-white text-[#4A5A53] border-[#E5E1D8] hover:border-[#7E9979]'}`}
                    data-testid={`coach-pin-btn-${i}`}
                  >
                    {m.saved ? <PinOff className="w-3 h-3" strokeWidth={1.8} /> : <BookmarkPlus className="w-3 h-3" strokeWidth={1.8} />}
                    {m.saved ? 'Pinned' : 'Pin to My Routines'}
                  </button>
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="text-sm text-[#4A5A53] flex items-center gap-2 font-light"><Loader2 className="w-4 h-4 animate-spin" /> thinking…</div>
          )}
          <div ref={endRef} />
        </div>

        <div className="flex flex-wrap gap-2 pt-2" data-testid="coach-suggestion-chips">
          {suggestions.map((s) => (
            <button
              key={s.label}
              onClick={() => send(s.prompt)}
              disabled={loading}
              className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E1D8] hover:border-[#7E9979] text-[#4A5A53]"
              data-testid={`coach-chip-${s.label.split(' ')[0].toLowerCase()}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="Ask about meals, workouts or stretching…"
            className="h-11 bg-white border-[#E5E1D8] rounded-full"
            data-testid="coach-chat-input"
          />
          <MicButton testId="coach-chat-mic" onText={(t) => setInput((v) => (v ? `${v} ${t}` : t))} />
          <Button onClick={() => send()} disabled={loading || !input.trim()} className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] h-11 w-11 p-0" data-testid="coach-chat-send">
            <Send className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CoachChat;
